/**
 * IoT Shack API Client
 */

import { cpFetch, edgeFetchWithAuth } from '../../lib/cpClient';
import { getJwt } from '@/lib/api';
import { readDcAuthFromStorage } from '@/lib/auth';
import { isMissingIotDevice } from './deviceLookupHonesty';
import type { DashboardData, Device, DeviceModel, Gateway, Decoder, Profile, DecoderTestResult, Listener, IoTDashboard, FleetHealthResponse, MessageRateResponse, ModelInstancesResponse, PlaygroundResponse, IoTChatResponse, FleetSummaryResponse } from './types';

/** Same summary the sentinel reads (telemetry-read). Honest zeros when empty. */
export type QuarantineSummary = {
  success?: boolean;
  new_count?: number;
  oldest_age_seconds?: number | null;
  oldest_at?: string | null;
  decoder_unresolved_count?: number;
  channels?: { quarantine?: number; dlq?: number; triage?: number };
  top_sources?: Array<{ source: string; count: number }>;
  error?: string;
};

export async function fetchQuarantineSummary(): Promise<QuarantineSummary> {
  const token = getJwt() || undefined;
  const tenant = readDcAuthFromStorage()?.tenant || 'prod';
  const res = await edgeFetchWithAuth('/api/telemetry/quarantine-summary', token, String(tenant));
  if (!res.ok) {
    // Honest empty — do not throw a blank panel
    return { success: false, new_count: 0, oldest_age_seconds: null, decoder_unresolved_count: 0, error: `http_${res.status}` };
  }
  const body = (await res.json().catch(() => ({}))) as QuarantineSummary;
  const top = Array.isArray(body.top_sources) ? body.top_sources : [];
  const decoderFromSources = top
    .filter((t) => String(t.source || '').includes('decoder:unresolved'))
    .reduce((sum, t) => sum + Number(t.count || 0), 0);
  return {
    ...body,
    new_count: Number(body.new_count || 0),
    oldest_age_seconds:
      body.oldest_age_seconds == null ? null : Number(body.oldest_age_seconds),
    decoder_unresolved_count:
      body.decoder_unresolved_count != null
        ? Number(body.decoder_unresolved_count)
        : decoderFromSources,
  };
}

type PagedResult<T> = {
  items: T[];
  total?: number;
  page?: number;
  limit?: number;
  hasMore?: boolean;
};

/**
 * Dashboard
 */
export async function fetchDashboard(): Promise<DashboardData> {
  return cpFetch('/iot/dashboard');
}

/**
 * Devices
 */
export async function fetchDevices(): Promise<Device[]> {
  const response = await cpFetch('/iot/devices');
  if (Array.isArray(response)) return response;
  return Array.isArray((response as PagedResult<Device>)?.items) ? (response as PagedResult<Device>).items : [];
}

export async function fetchDevice(id: string): Promise<Device | null> {
  try {
    return await cpFetch(`/iot/devices/${encodeURIComponent(id)}`);
  } catch (err) {
    if (isMissingIotDevice(err)) return null;
    throw err;
  }
}

/**
 * Models (Catalog)
 */
export async function fetchModels(): Promise<DeviceModel[]> {
  const response = await cpFetch('/iot/models');
  if (Array.isArray(response)) return response;
  return Array.isArray((response as any)?.items) ? (response as any).items : [];
}

export async function fetchModel(id: string): Promise<DeviceModel> {
  return cpFetch(`/iot/models/${encodeURIComponent(id)}`);
}

export async function fetchModelInstances(id: string): Promise<ModelInstancesResponse> {
  return cpFetch(`/iot/models/${encodeURIComponent(id)}/instances`);
}

export async function createDevice(data: Partial<Device>): Promise<Device> {
  return cpFetch('/iot/devices', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function deleteDevice(id: string): Promise<void> {
  await cpFetch(`/iot/devices/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
}

/**
 * Gateways
 */
export async function fetchGateways(): Promise<Gateway[]> {
  const response = await cpFetch('/iot/gateways');
  if (Array.isArray(response)) return response;
  return Array.isArray((response as PagedResult<Gateway>)?.items) ? (response as PagedResult<Gateway>).items : [];
}

export async function fetchGateway(id: string): Promise<Gateway> {
  return cpFetch(`/iot/gateways/${encodeURIComponent(id)}`);
}

export async function createGateway(data: Partial<Gateway>): Promise<Gateway> {
  return cpFetch('/iot/gateways', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Decoders
 */
export async function fetchDecoders(): Promise<Decoder[]> {
  const response = await cpFetch('/iot/decoders');
  if (Array.isArray(response)) return response;
  return Array.isArray((response as PagedResult<Decoder>)?.items) ? (response as PagedResult<Decoder>).items : [];
}

export async function fetchDecoder(id: string): Promise<Decoder> {
  return cpFetch(`/iot/decoders/${encodeURIComponent(id)}`);
}

export async function createDecoder(data: Partial<Decoder>): Promise<Decoder> {
  return cpFetch('/iot/decoders', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function testDecoder(decoderId: string, payload: string, payloadType: 'hex' | 'base64' | 'json' = 'hex'): Promise<DecoderTestResult> {
  // Backend supports both:
  // - POST /decoders/:id/test (preferred)
  // - POST /decoders/test with { decoderId, ... } (legacy)
  return cpFetch(`/iot/decoders/${encodeURIComponent(decoderId)}/test`, {
    method: 'POST',
    body: JSON.stringify({ payload, payloadType })
  });
}

/**
 * Profiles
 */
export async function fetchProfiles(): Promise<Profile[]> {
  const response = await cpFetch('/iot/profiles');
  if (Array.isArray(response)) return response;
  return Array.isArray((response as PagedResult<Profile>)?.items) ? (response as PagedResult<Profile>).items : [];
}

/**
 * Listeners
 */
export async function fetchListeners(): Promise<Listener[]> {
  const response = await cpFetch('/iot/listeners');
  if (Array.isArray(response)) return response;
  return Array.isArray((response as PagedResult<Listener>)?.items) ? (response as PagedResult<Listener>).items : [];
}

export async function createListener(data: Partial<Listener>): Promise<Listener> {
  return cpFetch('/iot/listeners', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteListener(listenerId: string): Promise<void> {
  await cpFetch(`/iot/listeners/${encodeURIComponent(listenerId)}`, {
    method: 'DELETE',
  });
}

/**
 * Dashboards + Analytics
 */
export async function fetchIoTDashboards(): Promise<IoTDashboard[]> {
  const response = await cpFetch('/iot/dashboards');
  const items = (response as any)?.items;
  return Array.isArray(items) ? (items as IoTDashboard[]) : [];
}

export async function bootstrapIoTDashboards(): Promise<IoTDashboard[]> {
  const response = await cpFetch('/iot/dashboards/bootstrap-defaults', { method: 'POST' });
  const items = (response as any)?.items;
  return Array.isArray(items) ? (items as IoTDashboard[]) : [];
}

export async function fetchFleetHealth(params: { offlineAfterMinutes?: number } = {}): Promise<FleetHealthResponse> {
  const qs = new URLSearchParams();
  if (params.offlineAfterMinutes) qs.set('offlineAfterMinutes', String(params.offlineAfterMinutes));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return cpFetch(`/iot/analytics/fleet-health${suffix}`);
}

export async function fetchMessageRate(params: { windowMinutes?: number; bucketMinutes?: number } = {}): Promise<MessageRateResponse> {
  const qs = new URLSearchParams();
  if (params.windowMinutes) qs.set('windowMinutes', String(params.windowMinutes));
  if (params.bucketMinutes) qs.set('bucketMinutes', String(params.bucketMinutes));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return cpFetch(`/iot/analytics/message-rate${suffix}`);
}

export async function fetchProfile(id: string): Promise<Profile> {
  return cpFetch(`/iot/profiles/${encodeURIComponent(id)}`);
}

export async function createProfile(data: Partial<Profile>): Promise<Profile> {
  return cpFetch('/iot/profiles', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Dashboard Editor
 */
export async function fetchWidgetTypes(): Promise<{ widgetTypes: Array<any> }> {
  return cpFetch('/iot/dashboards/widget-types');
}

export async function addWidget(dashboardId: string, widget: any): Promise<IoTDashboard> {
  return cpFetch(`/iot/dashboards/${encodeURIComponent(dashboardId)}/widgets`, {
    method: 'POST',
    body: JSON.stringify(widget)
  });
}

export async function updateWidget(dashboardId: string, widgetId: string, updates: any): Promise<IoTDashboard> {
  return cpFetch(`/iot/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
}

export async function deleteWidget(dashboardId: string, widgetId: string): Promise<IoTDashboard> {
  return cpFetch(`/iot/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}`, {
    method: 'DELETE'
  });
}

export async function updateDashboardLayout(dashboardId: string, widgets: Array<any>): Promise<IoTDashboard> {
  return cpFetch(`/iot/dashboards/${encodeURIComponent(dashboardId)}/layout`, {
    method: 'PUT',
    body: JSON.stringify({ widgets })
  });
}

export async function fetchDeviceFreshness(params: {
  offlineAfterMinutes?: number;
  limit?: number;
  deviceIdPrefix?: string;
  deviceType?: string;
  gatewayId?: string;
  statusFilter?: 'online' | 'offline' | 'unknown' | 'all';
}): Promise<any> {
  const query = new URLSearchParams();
  if (params.offlineAfterMinutes) query.set('offlineAfterMinutes', String(params.offlineAfterMinutes));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.deviceIdPrefix) query.set('deviceIdPrefix', params.deviceIdPrefix);
  if (params.deviceType) query.set('deviceType', params.deviceType);
  if (params.gatewayId) query.set('gatewayId', params.gatewayId);
  if (params.statusFilter) query.set('statusFilter', params.statusFilter);
  
  return cpFetch(`/iot/analytics/device-freshness?${query.toString()}`);
}

export async function fetchTopNoisiestDevices(params: {
  windowMinutes?: number;
  limit?: number;
  deviceType?: string;
  gatewayId?: string;
}): Promise<any> {
  const query = new URLSearchParams();
  if (params.windowMinutes) query.set('windowMinutes', String(params.windowMinutes));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.deviceType) query.set('deviceType', params.deviceType);
  if (params.gatewayId) query.set('gatewayId', params.gatewayId);
  
  return cpFetch(`/iot/analytics/top-noisiest-devices?${query.toString()}`);
}

/**
 * Playground
 */
export async function fetchPlayground(params: {
  identifier: string;
  windowMinutes?: number;
  limit?: number;
  from?: string;
  to?: string;
}): Promise<PlaygroundResponse> {
  const query = new URLSearchParams();
  query.set('identifier', params.identifier);
  if (params.windowMinutes) query.set('windowMinutes', String(params.windowMinutes));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  return cpFetch(`/iot/playground?${query.toString()}`);
}

// ============================================================================
// AGENTIC CHAT
// ============================================================================

export async function sendIoTChatMessage(payload: {
  session_id?: string;
  input?: string;
  attachments?: any[];
  confirmation?: boolean;
  device_ids?: string[];
  retry_phase?: string;
}): Promise<IoTChatResponse> {
  return cpFetch('/iot/chat', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function loadIoTChatSession(sessionId: string): Promise<{ success: boolean; session: any }> {
  return cpFetch(`/iot/chat/session/${encodeURIComponent(sessionId)}`);
}

export async function loadIoTChatSessionByModel(modelId: string): Promise<{ success: boolean; session: any }> {
  return cpFetch(`/iot/chat/session-by-model/${encodeURIComponent(modelId)}`);
}

// ============================================================================
// FLEET SUMMARY
// ============================================================================

export async function fetchFleetSummary(params: { emittingMinutes?: number } = {}): Promise<FleetSummaryResponse> {
  const qs = new URLSearchParams();
  if (params.emittingMinutes) qs.set('emittingMinutes', String(params.emittingMinutes));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return cpFetch(`/iot/analytics/fleet-summary${suffix}`);
}

